import { format, parseISO } from 'date-fns';
import { Camera, Sunrise, Sunset, FileText } from 'lucide-react';

import { SCOUTING_TAGS } from '@/types/log-entry';
import type { LogEntry } from '@/types/log-entry';
import { formatTagLabel } from '@/utils/format-utils';
import { isSignEntry } from '@/utils/recent-scouts-tally';

/**
 * RecentScoutRow — single place-led "tracked kicker" row (DASH-S1 / #786).
 *
 * Icon-free: NO species/sighting-type glyph anywhere. The left tile is a 48×48
 * `rounded-md` photo thumbnail + count badge when photos exist, or a NEUTRAL
 * placeholder tile (faint generic notes mark, never a type glyph) when photo-less —
 * so sighting and sign rows share identical height in the mixed list.
 *
 * Kicker derivation:
 *  - SIGHTING (`animalObserved === true`): `animalSex` title-cased + qualifier.
 *    buck qualifier ← `animalAntlerDescription`; doe/other ← `animalAgeClass`
 *    (rendered VERBATIM, no synthesized "YR" — A2). `unknown` sex → `DEER`, no
 *    qualifier (A4).
 *  - SIGN (`animalObserved !== true`): RULE-K precedence-selected tag via
 *    `formatTagLabel`. The precedence list IS the `SCOUTING_TAGS` declaration
 *    order — iterated directly so it can never drift from source. RULE-Z: when
 *    no `SCOUTING_TAGS`-member tag is present on the entry, NO kicker element is
 *    rendered (covers empty `tags` AND non-member-only tags).
 *
 * Casing: the kicker renders title-cased text through `formatTagLabel` /
 * title-case and is uppercased at the render layer via the `field-label`
 * convention's `text-transform: uppercase` (G8) — never a hardcoded
 * pre-uppercased literal.
 */

interface RecentScoutRowProps {
  entry: LogEntry;
  /** Resolved waypoint name; falls back to "Unmarked spot" (A5). */
  waypointName?: string;
  onClick: (id: string) => void;
}

const UNMARKED_SPOT_FALLBACK = 'Unmarked spot';
const UNKNOWN_SEX_KICKER = 'Deer';
const TILE_PX = 48;

/**
 * RULE-K: select the highest-precedence tag present on the entry, using the
 * canonical `SCOUTING_TAGS` declaration order as the precedence list (referenced
 * directly so precedence can never drift). Returns `null` when no member tag is
 * present (RULE-Z fallback → no kicker). Includes `sighting`, so a sign row whose
 * only tag is `sighting` yields the "Sighting" kicker here (not RULE-Z).
 */
function selectSignKickerTag(tags: string[]): string | null {
  for (const candidate of SCOUTING_TAGS) {
    if (tags.includes(candidate)) {
      return candidate;
    }
  }
  return null;
}

/**
 * Derive the sighting kicker label (title-cased; uppercased at render via CSS).
 * Returns the type word + optional qualifier verbatim.
 */
function sightingKicker(entry: LogEntry): { label: string; qualifier: string | null } {
  switch (entry.animalSex) {
    case 'buck':
      return {
        label: 'Buck',
        qualifier: entry.animalAntlerDescription?.trim() || null,
      };
    case 'doe':
      return { label: 'Doe', qualifier: entry.animalAgeClass?.trim() || null };
    case 'fawn':
      return { label: 'Fawn', qualifier: entry.animalAgeClass?.trim() || null };
    default:
      // 'unknown' or unset → DEER, no qualifier (A4).
      return { label: UNKNOWN_SEX_KICKER, qualifier: null };
  }
}

const MIDDAY_HOUR = 12;

/** `true` when the `HH:MM` time falls before midday (morning sit → sunrise glyph). */
function isMorningTime(time: string): boolean {
  const hour = Number.parseInt(time.slice(0, 2), 10);
  return Number.isFinite(hour) && hour < MIDDAY_HOUR;
}

export function RecentScoutRow({ entry, waypointName, onClick }: RecentScoutRowProps) {
  const sign = isSignEntry(entry);

  // ── Kicker ────────────────────────────────────────────────────────────────
  let kickerLabel: string | null;
  let kickerQualifier: string | null = null;

  if (sign) {
    const tag = selectSignKickerTag(entry.tags);
    // RULE-Z: no SCOUTING_TAGS-member tag present → render no kicker element.
    kickerLabel = tag ? formatTagLabel(tag) : null;
  } else {
    const k = sightingKicker(entry);
    kickerLabel = k.label;
    kickerQualifier = k.qualifier;
  }

  // ── Headline (resolved waypoint name) ──────────────────────────────────────
  const headline = waypointName?.trim() || UNMARKED_SPOT_FALLBACK;

  // ── Subline ────────────────────────────────────────────────────────────────
  const note = entry.notes.trim();
  const behavior = entry.animalBehavior?.trim();
  const subline = !sign && behavior
    ? note
      ? `${behavior} · ${note}`
      : behavior
    : note || null;

  // ── Left tile ───────────────────────────────────────────────────────────────
  const photoCount = entry.photos?.length ?? 0;
  const hasPhotos = photoCount > 0;

  // ── Right rail ────────────────────────────────────────────────────────────────
  const hasTime = Boolean(entry.time);
  const morning = hasTime ? isMorningTime(entry.time as string) : false;
  const dateLabel = format(parseISO(entry.date), 'MMM d');

  return (
    <button
      type="button"
      onClick={() => onClick(entry.id)}
      className="w-full text-left flex items-center gap-3 px-4 py-3 border-b-[0.5px] border-[var(--color-border-subtle)] last:border-b-0 hover:bg-[var(--color-elevated)] transition-colors"
      data-testid={`recent-scout-row-${entry.id}`}
      data-row-kind={sign ? 'sign' : 'sighting'}
    >
      {/* Left tile — photo thumbnail OR neutral placeholder (never a type glyph) */}
      <div
        className="relative shrink-0 rounded-[var(--radius-md)] border-[0.5px] border-[var(--color-border)] overflow-hidden bg-[var(--color-elevated)] flex items-center justify-center"
        style={{ width: TILE_PX, height: TILE_PX }}
        data-testid="recent-scout-tile"
        data-has-photos={hasPhotos ? 'true' : 'false'}
      >
        {hasPhotos ? (
          <>
            {/* Static thumbnail placeholder fill — image source wiring is out of
                scope (photo refs are ids; gallery resolves blobs elsewhere). */}
            <span
              className="absolute inset-0 bg-[var(--color-bark-700)]"
              aria-hidden
            />
            <span
              className="absolute -bottom-1 -right-1 flex items-center gap-0.5 rounded-full bg-[var(--color-bark-900)] border-[0.5px] border-[var(--color-border)] px-1 py-0.5"
              data-testid="recent-scout-photo-badge"
            >
              <Camera size={10} aria-hidden className="text-[var(--color-text-secondary)]" />
              <span className="text-[var(--color-text-secondary)] tabular-nums" style={{ fontSize: '9.5px' }}>
                {photoCount}
              </span>
            </span>
          </>
        ) : (
          <FileText
            size={18}
            aria-hidden
            className="text-[var(--color-text-placeholder)]"
            data-testid="recent-scout-placeholder-mark"
          />
        )}
      </div>

      {/* Main column */}
      <div className="flex-1 min-w-0">
        {kickerLabel !== null && (
          <p className="field-label" data-testid="recent-scout-kicker">
            {kickerLabel}
            {kickerQualifier && (
              <>
                {' · '}
                <span className="tabular-nums">{kickerQualifier}</span>
              </>
            )}
          </p>
        )}
        <p
          className="text-[var(--text-body-md)] font-semibold text-[var(--color-text-primary)] mt-0.5 truncate"
          data-testid="recent-scout-headline"
        >
          {headline}
        </p>
        {subline && (
          <p
            className="text-[var(--text-body-sm)] text-[var(--color-text-muted)] mt-0.5 truncate"
            data-testid="recent-scout-subline"
          >
            {subline}
          </p>
        )}
      </div>

      {/* Right rail — time-of-day glyph + HH:MM over Mon D, or date-only fallback */}
      <div className="flex flex-col items-end gap-1 shrink-0" data-testid="recent-scout-rail">
        {hasTime ? (
          <>
            <span className="flex items-center gap-1 text-[var(--color-text-muted)]">
              {morning ? (
                <Sunrise size={13} aria-hidden />
              ) : (
                <Sunset size={13} aria-hidden />
              )}
              <span className="tabular-nums text-[var(--text-body-sm)]">{entry.time}</span>
            </span>
            <span className="text-[var(--text-body-xs)] text-[var(--color-text-placeholder)] tabular-nums">
              {dateLabel}
            </span>
          </>
        ) : (
          <span
            className="text-[var(--text-body-xs)] text-[var(--color-text-placeholder)] tabular-nums"
            data-testid="recent-scout-date-only"
          >
            {dateLabel}
          </span>
        )}
      </div>
    </button>
  );
}
